import type {
  AccountType, AmRole, Assignment, CallOutcome, Customer, LogEntry,
  NewBizProspect, NewBizSource, PipelineStatus
} from './types';

// Re-export for server-side imports that use domain.ts as a single boundary.
export type { AccountType, AmRole, CallOutcome, NewBizSource, PipelineStatus };

export const SENIOR_AMS = ['Jaidan-Sai Patel', 'Steve Clarke'];
export const JUNIOR_AMS = ['Angad Dhadyalla', 'Mukesh Arunachalam'];

export const TARGET_CALLS = 10;
export const CYCLE_DAYS = 14;
export const ACTIVITY_STALE_MS = 7 * 86_400_000;
export const HIGH_VALUE_THRESHOLD = 20_000;

export function amRoleOf(name: string): AmRole {
  return SENIOR_AMS.includes(name) ? 'senior' : 'junior';
}

export function classifyAccount(c: Customer): AccountType {
  const t24 = c.t2024 || 0, t25 = c.t2025 || 0;
  if (t25 === 0 && t24 > 0) return 'dormant';
  if (t24 === 0 && t25 > 0) return 'newbiz';
  if (t25 > 0 && t24 > 0) return 'existing';
  return 'dormant';
}

export type AccountTrend = 'growing' | 'stable' | 'declining';
export function accountTrend(c: Customer): AccountTrend {
  const t24 = c.t2024 || 0, t25 = c.t2025 || 0;
  if (t25 > t24 * 1.05) return 'growing';
  if (t25 < t24 * 0.95) return 'declining';
  return 'stable';
}

export type Priority = 'high' | 'medium' | 'low';
export function priorityScore(c: Customer, lastContactTs: number): Priority {
  let score = 0;
  const t24 = c.t2024 || 0, t25 = c.t2025 || 0;
  if (t24 > 50_000) score += 3;
  else if (t24 > 10_000) score += 2;
  else if (t24 > 1_000) score += 1;
  if (t25 < t24 * 0.9) score += 2;
  if (t25 === 0 && t24 > 0) score += 3;
  if (!lastContactTs || Date.now() - lastContactTs > ACTIVITY_STALE_MS) score += 1;
  if (score >= 6) return 'high';
  if (score >= 3) return 'medium';
  return 'low';
}

export function deadlineFrom(fromTs: number): number {
  const d = new Date(fromTs);
  d.setDate(d.getDate() + CYCLE_DAYS);
  return d.getTime();
}

export interface DeadlineInfo {
  cls: 'dl-ok' | 'dl-warn' | 'dl-overdue';
  label: string;
  overdue: boolean;
}
export function deadlineInfo(dl: number): DeadlineInfo {
  const diff = dl - Date.now();
  const days = Math.ceil(diff / 86_400_000);
  if (diff < 0) return { cls: 'dl-overdue', label: `⚠️ ${Math.abs(days)}d overdue`, overdue: true };
  if (days <= 2) return { cls: 'dl-warn', label: `⏰ ${days}d left`, overdue: false };
  return { cls: 'dl-ok', label: `✓ ${days}d left`, overdue: false };
}

export function fmtCcy(v: number | null | undefined): string {
  if (!v || v === 0) return '—';
  return '£' + Number(v).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function fmtDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function startOfTodayMs(): number {
  const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime();
}

// Smart filter set used in pipeline page.
export const SMART_FILTERS: Array<{ id: SmartFilterId; label: string }> = [
  { id: 'all', label: 'All Accounts' },
  { id: 'dormant', label: '💤 Dormant' },
  { id: 'existing', label: '✅ Existing' },
  { id: 'newbiz', label: '✨ New Business' },
  { id: 'overdue', label: '⚠️ Overdue' },
  { id: 'no_activity', label: '😴 No Activity 7d' },
  { id: 'high_value', label: '💰 High Value' },
  { id: 'assigned_me', label: '📌 Assigned to Me' },
  { id: 'won', label: '🏆 Won' },
  { id: 'lost', label: '❌ Lost' }
];
export type SmartFilterId =
  | 'all' | 'dormant' | 'existing' | 'newbiz' | 'overdue'
  | 'no_activity' | 'high_value' | 'assigned_me' | 'won' | 'lost';

export interface PipelineRow {
  id: string;
  amName: string;
  custId: string | null;
  custName: string;
  accountType: AccountType | 'newbiz';
  status: PipelineStatus;
  outcome: CallOutcome | null;
  deadline: number;
  revenue: number;
  notes: string;
  assignedDate: number;
  wonDate: number | null;
  source: 'assign' | 'newbiz';
}

export function toPipelineRows(assigns: Assignment[], nb: NewBizProspect[]): PipelineRow[] {
  const a = assigns.map<PipelineRow>(x => ({
    id: x.id, amName: x.amName, custId: x.custId, custName: x.custName,
    accountType: x.accountType, status: x.status, outcome: x.outcome,
    deadline: x.deadline, revenue: x.revenue, notes: x.notes,
    assignedDate: x.assignedDate, wonDate: x.wonDate, source: 'assign'
  }));
  const n = nb.map<PipelineRow>(x => ({
    id: x.id, amName: x.amName, custId: null, custName: x.companyName,
    accountType: 'newbiz', status: x.status, outcome: x.outcome,
    deadline: x.deadline, revenue: x.revenue, notes: x.notes,
    assignedDate: x.assignedDate, wonDate: x.wonDate, source: 'newbiz'
  }));
  return [...a, ...n];
}

export function applySmartFilter(
  filter: SmartFilterId,
  rows: PipelineRow[],
  custs: Customer[],
  logs: LogEntry[],
  meName: string
): PipelineRow[] {
  const now = Date.now();
  switch (filter) {
    case 'dormant':  return rows.filter(a => a.accountType === 'dormant');
    case 'existing': return rows.filter(a => a.accountType === 'existing');
    case 'newbiz':   return rows.filter(a => a.source === 'newbiz' || a.accountType === 'newbiz');
    case 'overdue':  return rows.filter(a => a.deadline < now && a.status !== 'won' && a.status !== 'lost');
    case 'no_activity': {
      const active7 = new Set(logs.filter(l => l.ts >= now - ACTIVITY_STALE_MS).map(l => l.customerId));
      return rows.filter(a => a.custId && !active7.has(a.custId));
    }
    case 'high_value': {
      const hv = new Set(custs.filter(c => (c.t2024 || 0) > HIGH_VALUE_THRESHOLD).map(c => c.id));
      return rows.filter(a => a.custId && hv.has(a.custId));
    }
    case 'assigned_me': return rows.filter(a => a.amName === meName);
    case 'won':         return rows.filter(a => a.status === 'won');
    case 'lost':        return rows.filter(a => a.status === 'lost');
    default:            return rows;
  }
}

export interface Alert { type: 'overdue' | 'warn' | 'info'; msg: string }
export function buildAlerts(amName: string | null, assigns: Assignment[], nb: NewBizProspect[], logs: LogEntry[]): Alert[] {
  const now = Date.now();
  const a = assigns.filter(x => amName === null || x.amName === amName);
  const n = nb.filter(x => amName === null || x.amName === amName);
  const all = [...a, ...n];
  const out: Alert[] = [];

  const overdue = all.filter(x => x.deadline < now && x.status !== 'won' && x.status !== 'lost');
  if (overdue.length > 0)
    out.push({ type: 'overdue', msg: `${overdue.length} assignment${overdue.length > 1 ? 's are' : ' is'} overdue` });

  const noAct7 = a.filter(x => {
    const last = Math.max(0, ...logs.filter(l => l.customerId === x.custId).map(l => l.ts));
    return last < now - ACTIVITY_STALE_MS && x.status !== 'won' && x.status !== 'lost';
  });
  if (noAct7.length > 0)
    out.push({ type: 'warn', msg: `${noAct7.length} account${noAct7.length > 1 ? 's have' : ' has'} had no activity for 7+ days` });

  const followToday = all.filter(x => x.status === 'followup');
  if (followToday.length > 0)
    out.push({ type: 'info', msg: `${followToday.length} account${followToday.length > 1 ? 's require' : ' requires'} follow-up` });

  return out;
}

// Badge label maps (for UI rendering)
export const ACCOUNT_TYPE_BADGE: Record<AccountType | 'declining' | 'growing', { cls: string; label: string }> = {
  dormant:   { cls: 'at-dormant',   label: '💤 Dormant' },
  existing:  { cls: 'at-existing',  label: '✅ Existing' },
  newbiz:    { cls: 'at-newbiz',    label: '✨ New Biz' },
  declining: { cls: 'at-declining', label: '📉 Declining' },
  growing:   { cls: 'at-growing',   label: '📈 Growing' }
};
export const PIPELINE_BADGE: Record<PipelineStatus, { cls: string; label: string }> = {
  not_started: { cls: 'ps-notstarted', label: '○ Not Started' },
  researching: { cls: 'ps-researching', label: '🔍 Researching' },
  researched:  { cls: 'ps-researched', label: '📋 Researched' },
  called:      { cls: 'ps-called', label: '📞 Called' },
  followup:    { cls: 'ps-followup', label: '🔄 Follow-up' },
  quoted:      { cls: 'ps-quoted', label: '💬 Quoted' },
  won:         { cls: 'ps-won', label: '✅ Won' },
  lost:        { cls: 'ps-lost', label: '❌ Lost' }
};
export const OUTCOME_BADGE: Record<CallOutcome, { cls: string; label: string }> = {
  no_answer:    { cls: 'oc-noanswer', label: 'No Answer' },
  no_interest:  { cls: 'oc-noint', label: 'No Interest' },
  interested:   { cls: 'oc-interested', label: 'Interested' },
  followup_req: { cls: 'oc-followup', label: 'Follow-up' },
  quote_req:    { cls: 'oc-quoted', label: 'Quote Req.' },
  won:          { cls: 'oc-won', label: 'Won' },
  lost:         { cls: 'oc-lost', label: 'Lost' }
};
export const PRIORITY_BADGE: Record<Priority, { cls: string; label: string }> = {
  high:   { cls: 'pri-high', label: '🔴 High' },
  medium: { cls: 'pri-medium', label: '🟡 Medium' },
  low:    { cls: 'pri-low', label: '🟢 Low' }
};

export const PIPELINE_STATUS_OPTIONS: Array<{ value: PipelineStatus; label: string }> = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'researching', label: 'Researching' },
  { value: 'researched',  label: 'Researched' },
  { value: 'called',      label: 'Called' },
  { value: 'followup',    label: 'Follow-up Required' },
  { value: 'quoted',      label: 'Quoted' },
  { value: 'won',         label: 'Won ✓' },
  { value: 'lost',        label: 'Lost ✗' }
];

export const OUTCOME_OPTIONS: Array<{ value: CallOutcome; label: string }> = [
  { value: 'no_answer',    label: 'No Answer' },
  { value: 'no_interest',  label: 'Spoke – No Interest' },
  { value: 'interested',   label: 'Spoke – Interested' },
  { value: 'followup_req', label: 'Follow-up Required' },
  { value: 'quote_req',    label: 'Quote Requested' },
  { value: 'won',          label: 'Won' },
  { value: 'lost',         label: 'Lost' }
];

export const SOURCE_OPTIONS: Array<{ value: NewBizSource; label: string }> = [
  { value: '', label: '— How was this prospect found? —' },
  { value: 'referral',  label: 'Referral' },
  { value: 'linkedin',  label: 'LinkedIn' },
  { value: 'cold_call', label: 'Cold Call Research' },
  { value: 'website',   label: 'Website Enquiry' },
  { value: 'event',     label: 'Event / Trade Show' },
  { value: 'other',     label: 'Other' }
];
